import { useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'
import { GridCellParams, GridColDef, GridFilterItem, GridFilterModel, GridRowParams } from '@mui/x-data-grid/models'
import { EventId } from '../../interfaces/IEvent'
import {
  CategoryAll,
  CriticalityAll,
  EventStatus,
  EventStatusAll,
  IncidentCategory,
  IncidentCriticality,
  IncidentStatus,
  PlannedWorkCategory,
  PlannedWorkCriticality,
  PlannedWorkStatus,
} from '../../interfaces/IEventEnums'
import { eventIdTextColor, toDateTime } from '../../utils/ValueFormatters'
import CustomPagination from '../../components/DataGrid/Pagination'
import { CategoryChip, CriticalityChip, StatusChip } from '../../components/Chip/EventChip'
import { StripedDataGrid, StyledGridOverlay } from '../../components/DataGrid/StyledComponents'
import { useAppContext } from '../../context/AppSettingsContextProvider'
import { EventsContext } from './EventsProvider'
import { nbNO } from '@mui/x-data-grid/locales'
import { CustomToolbar } from './Toolbar'
import { SEARCH_TYPE_LABELS } from '../../interfaces/IAffectedEntitySearch'

interface Props {
  goToEvent: (eventId: EventId) => void
}

function EventDataGrid({ goToEvent }: Props) {
  const theme = useTheme()
  const { handleFilterChange, loadingEvents, filteredEvents, selectedFilter, executedSearchCriteria } = useContext(EventsContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const criticalityParam = searchParams.get('criticality')
  const categoryParam = searchParams.get('category')

  const noRowsMessage =
    selectedFilter === 'search' && executedSearchCriteria && executedSearchCriteria.searchTerm.trim()
      ? `Ingen hendelser med ${SEARCH_TYPE_LABELS[executedSearchCriteria.searchType]} = "${executedSearchCriteria.searchTerm}".`
      : 'Ingen hendelser funnet'
  const { dataGridColumnState, setDataGridColumnState, showToDateExpired } = useAppContext()

  const statusValues = () => {
    switch (selectedFilter) {
      case 'generic':
        return Object.values(EventStatus)
      case 'planned':
        return Object.values(PlannedWorkStatus)
      case 'incident':
        return Object.values(IncidentStatus)
      default:
        return Object.values(EventStatusAll)
    }
  }

  const categoryValues = () => {
    switch (selectedFilter) {
      case 'generic':
        return []
      case 'planned':
        return Object.values(PlannedWorkCategory)
      case 'incident':
        return Object.values(IncidentCategory)
      default:
        return Object.values(CategoryAll)
    }
  }

  const criticalityValues = () => {
    switch (selectedFilter) {
      case 'generic':
        return []
      case 'planned':
        return Object.values(PlannedWorkCriticality)
      case 'incident':
        return Object.values(IncidentCriticality)
      default:
        return Object.values(CriticalityAll)
    }
  }

  const validValuesForField = (field: string): readonly string[] | null => {
    if (field === 'status') return statusValues() as readonly string[]
    if (field === 'category') return categoryValues() as readonly string[]
    if (field === 'criticality') return criticalityValues() as readonly string[]
    return null
  }

  const clearInvalidValues = (items: GridFilterItem[]): GridFilterItem[] => {
    return items.map((item) => {
      if (item.value === undefined || item.value === null || item.value === '') return item
      const valid = validValuesForField(item.field)
      if (valid === null || valid.includes(item.value as string)) return item
      return { ...item, value: undefined }
    })
  }

  const dropInvalidItems = (items: GridFilterItem[]): GridFilterItem[] => {
    return items.filter((item) => {
      if (item.value === undefined || item.value === null || item.value === '') return true
      const valid = validValuesForField(item.field)
      return valid === null || valid.includes(item.value as string)
    })
  }

  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => {
    const items: GridFilterItem[] = []
    if (criticalityParam) items.push({ id: 'scope-criticality', field: 'criticality', operator: 'is', value: criticalityParam })
    if (categoryParam) items.push({ id: 'scope-category', field: 'category', operator: 'is', value: categoryParam })
    return { items }
  })

  useEffect(() => {
    setFilterModel((prev) => {
      const isInProgress = (i: GridFilterItem) => i.value === undefined || i.value === null || i.value === ''
      const preserved = prev.items.filter((i) => isInProgress(i) || (i.field !== 'criticality' && i.field !== 'category'))
      const scope: GridFilterItem[] = []
      if (criticalityParam) scope.push({ id: 'scope-criticality', field: 'criticality', operator: 'is', value: criticalityParam })
      if (categoryParam) scope.push({ id: 'scope-category', field: 'category', operator: 'is', value: categoryParam })
      const combined = [...preserved, ...scope]
      const sameContent =
        combined.length === prev.items.length &&
        combined.every((it, idx) => it.field === prev.items[idx].field && it.value === prev.items[idx].value)
      if (sameContent) return prev
      return { ...prev, items: combined }
    })
  }, [criticalityParam, categoryParam])

  const activeFilterModel = useMemo<GridFilterModel>(
    () => ({ ...filterModel, items: dropInvalidItems(filterModel.items) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterModel, selectedFilter]
  )

  const handleFilterModelChange = (next: GridFilterModel) => {
    const cleaned = clearInvalidValues(next.items)
    setFilterModel({ ...next, items: cleaned })
    const nextCriticality = cleaned.find((i) => i.field === 'criticality')?.value ?? null
    const nextCategory = cleaned.find((i) => i.field === 'category')?.value ?? null
    if (nextCriticality === criticalityParam && nextCategory === categoryParam) return

    const params = new URLSearchParams(searchParams)
    if (nextCriticality) params.set('criticality', String(nextCriticality))
    else params.delete('criticality')
    if (nextCategory) params.set('category', String(nextCategory))
    else params.delete('category')
    setSearchParams(params, { replace: true })
  }

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Id',
      minWidth: 85,
      width: 85,
      renderCell: (params) => {
        return (
          <a
            href={`/events/${params.value}`}
            style={{
              textDecoration: 'none',
              fontWeight: 'bold',
              color: eventIdTextColor(params.value, theme),
            }}
          >
            {params.value}
          </a>
        )
      },
    },
    { field: 'externalReference', headerName: 'Eksternref', minWidth: 110, width: 110 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      type: 'singleSelect',
      getOptionValue: (value: any) => value?.value || '',
      getOptionLabel: (value: any) => value?.value || '',
      valueOptions: statusValues().map((status) => ({ value: status, label: status })),
      valueGetter: (value, row, column, apiRef) => value || '',
      renderCell: (params) => {
        return <StatusChip status={params.value as EventStatus} />
      },
    },
    {
      field: 'category',
      headerName: 'Kategori',
      minWidth: 110,
      width: 145,
      type: 'singleSelect',
      getOptionValue: (value: any) => value?.value || '',
      getOptionLabel: (value: any) => value?.value || '',
      valueOptions: categoryValues().map((category) => ({ value: category, label: category })),
      valueGetter: (value) => {
        return value === 'Udefinert' ? '' : value
      },
      renderCell: (params) => {
        return params.value ? <CategoryChip category={params.value as IncidentCategory | PlannedWorkCategory} /> : null
      },
    },
    {
      field: 'criticality',
      headerName: 'Alvorlighet',
      minWidth: 100,
      width: 100,
      type: 'singleSelect',
      getOptionValue: (value: any) => value?.value || '',
      getOptionLabel: (value: any) => value?.value || '',
      valueOptions: criticalityValues().map((criticality) => ({ value: criticality, label: criticality })),
      valueGetter: (value) => {
        return value === 'Udefinert' ? '' : value
      },
      renderCell: (params) => {
        return params.value ? <CriticalityChip criticality={params.value as IncidentCriticality | PlannedWorkCriticality} /> : null
      },
    },
    { field: 'title', headerName: 'Tittel', minWidth: 300, flex: 1 },
    {
      field: 'affected',
      headerName: 'Berørte',
      minWidth: 110,
      width: 110,
      type: 'number',
      valueGetter: (value, row, column, apiRef) => row.affectedEntitiesCount ?? 0,
    },
    { field: 'responsible', headerName: 'Ansvarlig', width: 180, valueFormatter: (value, row, column, apiRef) => row.responsible.name ?? '' },
    {
      field: 'fromDate',
      headerName: 'Starttid',
      minWidth: 110,
      width: 160,
      type: 'date',
      valueFormatter: (value, row, column, apiRef) => (value ? toDateTime(new Date(value as Date)) : null),
      valueGetter: (value, row, column, apiRef) => {
        return value ? new Date(value as Date) : null
      },
    },
    {
      field: 'toDate',
      headerName: 'Sluttid',
      minWidth: 110,
      width: 160,
      type: 'date',
      valueFormatter: (value, row, column, apiRef) => (value ? toDateTime(new Date(value as Date)) : null),
      valueGetter: (value, row, column, apiRef) => {
        return value ? new Date(value as Date) : null
      },
      cellClassName: (params: GridCellParams<any, Date>) =>
        params.value && params.row.status !== EventStatus.Lukket && new Date(params.value as Date) < new Date() && showToDateExpired
          ? 'toDate-expired'
          : '',
    },
    {
      field: 'createdOn',
      headerName: 'Opprettet',
      minWidth: 110,
      width: 160,
      type: 'date',
      valueFormatter: (value, row, column, apiRef) => (value ? toDateTime(new Date(value as Date)) : null),
      valueGetter: (value, row, column, apiRef) => {
        return value ? new Date(value as Date) : null
      },
    },
    {
      field: 'closedDate',
      headerName: 'Lukket',
      minWidth: 110,
      width: 160,
      type: 'date',
      valueFormatter: (value, row, column, apiRef) => (value ? toDateTime(new Date(value as Date)) : null),
      valueGetter: (value, row, column, apiRef) => {
        return value ? new Date(value as Date) : null
      },
    },
  ]

  return (
    <Box
      sx={{
        '& .toDate-expired': {
          border: `2px solid ${theme.palette.warning.main} !important`,
        },
      }}
    >
      <StripedDataGrid
        label="Hendelser"
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        loading={loadingEvents}
        onRowDoubleClick={(params: GridRowParams) => goToEvent(params.row.id)}
        rows={filteredEvents ?? []}
        filterModel={activeFilterModel}
        onFilterModelChange={handleFilterModelChange}
        columns={columns}
        showToolbar={true}
        autoHeight
        showCellVerticalBorder={true}
        showColumnVerticalBorder={true}
        hideFooterSelectedRowCount={true}
        rowHeight={45}
        columnHeaderHeight={45}
        onColumnVisibilityModelChange={(params) => {
          setDataGridColumnState(params)
        }}
        slots={{
          noRowsOverlay: () => <StyledGridOverlay>{noRowsMessage}</StyledGridOverlay>,
          pagination: CustomPagination,
          toolbar: CustomToolbar,
        }}
        slotProps={{
          filterPanel: {
            sx: { maxWidth: '90vw' },
          },
          toolbar: {
            onFilterChange: handleFilterChange,
          },
        }}
        initialState={{
          columns: {
            columnVisibilityModel: dataGridColumnState,
          },
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
      />
    </Box>
  )
}

export default EventDataGrid
